// DashboardPage.js — FX Capital India | Treasury Dashboard
// Live rates from fx.fxcapitalindia.in directly — no DLL dependency for rates
import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import CONFIG from '../config';

const BILL   = CONFIG.API + '/api/bills';
const FX_API = 'https://fx.fxcapitalindia.in/api';

const fmtFC = (v) => {
  const n = Number(v);
  if (isNaN(n) || n === 0) return '—';
  if (n >= 10000000) return (n / 10000000).toFixed(2) + 'Cr';
  if (n >= 100000)   return (n / 100000).toFixed(2) + 'L';
  return n.toLocaleString('en-IN', { maximumFractionDigits: 0 });
};

const fmtINR = (v) => {
  const n = Number(v);
  if (isNaN(n)) return '—';
  const abs  = Math.abs(n);
  const sign = n < 0 ? '−' : '+';
  if (abs >= 10000000) return sign + '₹' + (abs / 10000000).toFixed(2) + 'Cr';
  if (abs >= 100000)   return sign + '₹' + (abs / 100000).toFixed(2) + 'L';
  return sign + '₹' + abs.toLocaleString('en-IN', { maximumFractionDigits: 0 });
};

const fmtRate = (v, d = 4) => {
  const n = Number(v);
  return (!n || n === 0) ? '—' : n.toFixed(d);
};

const PAIRS = [
  { pair: 'USDINR', label: 'USD' },
  { pair: 'EURINR', label: 'EUR' },
  { pair: 'GBPINR', label: 'GBP' },
  { pair: 'AEDINR', label: 'AED' },
];

const C = {
  navy:    '#0D1B2A',
  blue:    '#1F4E79',
  gold:    '#C9A84C',
  green:   '#16A34A',
  red:     '#DC2626',
  amber:   '#8A5300',
  purple:  '#5b21b6',
  muted:   '#94a3b8',
  border:  '#e2e8f0',
  surface: '#f8fafc',
  white:   '#ffffff',
};

const Pill = ({ label, bg, color }) => (
  <span style={{
    fontSize: 10, padding: '2px 8px', borderRadius: 20,
    background: bg, color, fontWeight: 600
  }}>{label}</span>
);

const SectionTitle = ({ text }) => (
  <div style={{
    fontSize: 13, fontWeight: 700, color: C.blue, marginBottom: 8,
    display: 'flex', alignItems: 'center', gap: 6
  }}>
    <span style={{
      width: 3, height: 14, background: C.gold,
      borderRadius: 2, display: 'inline-block'
    }} />
    {text}
  </div>
);

// ── LIVE RATE STRIP ──────────────────────────────────────────────────────
const RateStrip = ({ rates }) => (
  <div style={{
    background: C.navy, padding: '8px 16px',
    display: 'flex', gap: 0, overflowX: 'auto',
    borderBottom: `2px solid ${C.gold}33`
  }}>
    {PAIRS.map(p => {
      const r = rates?.[p.pair];
      return (
        <div key={p.pair} style={{
          flex: '0 0 auto', padding: '0 20px 0 0',
          borderRight: `1px solid #ffffff22`,
          marginRight: 20, minWidth: 100
        }}>
          <div style={{ fontSize: 10, color: C.muted, letterSpacing: 1 }}>
            {p.label}
          </div>
          <div style={{
            fontSize: 16, fontWeight: 700,
            color: r ? C.gold : C.muted,
            fontVariantNumeric: 'tabular-nums'
          }}>
            {r ? fmtRate(r.ltp, 4) : 'offline'}
          </div>
          {r && (
            <div style={{
              fontSize: 9,
              color: r.change >= 0 ? '#4ade80' : '#f87171'
            }}>
              {r.change >= 0 ? '▲' : '▼'} {Math.abs(r.change || 0).toFixed(4)}
            </div>
          )}
        </div>
      );
    })}
    <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center' }}>
      <div style={{
        width: 6, height: 6, borderRadius: '50%',
        background: rates && Object.keys(rates).length > 0 ? '#4ade80' : '#f87171',
        marginRight: 6
      }} />
      <span style={{ fontSize: 10, color: C.muted }}>
        {rates && Object.keys(rates).length > 0 ? 'Live' : 'Offline'}
      </span>
    </div>
  </div>
);

// ── ALL CURRENCY EXPOSURE TABLE ──────────────────────────────────────────
const ExposureTable = ({ kpis2, rates }) => {
  const rows = kpis2?.byCurrency || [];
  return (
    <div style={{
      background: C.white, border: `0.5px solid ${C.border}`,
      borderRadius: 10, overflow: 'hidden', marginBottom: 10
    }}>
      <div style={{
        background: C.navy, padding: '7px 14px',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center'
      }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: C.white }}>
          All Currency Exposure
        </span>
        <span style={{ fontSize: 11, color: C.gold, fontWeight: 600 }}>
          Total: {fmtINR(kpis2?.totalOpenExposureInr || 0).replace('+', '')}
        </span>
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <thead>
          <tr style={{ background: C.surface }}>
            {['Currency', 'FC Open', 'Avg Cost', 'Live Rate', 'MTM'].map(h => (
              <th key={h} style={{
                padding: '6px 12px',
                textAlign: h === 'Currency' ? 'left' : 'right',
                fontSize: 10, color: C.muted, fontWeight: 600,
                textTransform: 'uppercase', letterSpacing: 0.4,
                borderBottom: `1px solid ${C.border}`
              }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={5} style={{ padding: 14, color: C.muted, fontSize: 12 }}>
                No open exposure
              </td>
            </tr>
          ) : rows.map((r, i) => {
            const liveRate = rates?.[r.currency + 'INR']?.ltp;
            const liveMtm  = liveRate && r.avgCostRate
              ? (liveRate - r.avgCostRate) * r.fcOpen
              : r.mtmInr;
            return (
              <tr key={i} style={{
                borderBottom: `0.5px solid ${C.surface}`,
                background: i % 2 === 0 ? C.white : C.surface
              }}>
                <td style={{ padding: '7px 12px', fontWeight: 700, color: C.blue }}>
                  {r.currency}
                </td>
                <td style={{ padding: '7px 12px', textAlign: 'right' }}>
                  {fmtFC(r.fcOpen)}
                </td>
                <td style={{ padding: '7px 12px', textAlign: 'right', color: C.muted }}>
                  {fmtRate(r.avgCostRate)}
                </td>
                <td style={{ padding: '7px 12px', textAlign: 'right', color: C.gold, fontWeight: 600 }}>
                  {liveRate ? fmtRate(liveRate) : '—'}
                </td>
                <td style={{
                  padding: '7px 12px', textAlign: 'right', fontWeight: 700,
                  color: liveMtm >= 0 ? C.green : C.red
                }}>
                  {liveRate ? fmtINR(liveMtm) : '—'}
                </td>
              </tr>
            );
          })}
          {rows.length > 1 && (
            <tr style={{ background: '#eff6ff', borderTop: `1px solid ${C.border}` }}>
              <td style={{ padding: '7px 12px', fontWeight: 700, color: C.navy }}>Total INR</td>
              <td colSpan={3} />
              <td style={{
                padding: '7px 12px', textAlign: 'right', fontWeight: 700,
                color: (kpis2?.portfolioMtmInr || 0) >= 0 ? C.green : C.red
              }}>
                {fmtINR(kpis2?.portfolioMtmInr || 0)}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

// ── HEDGE RATIO ──────────────────────────────────────────────────────────
const HedgeRatio = ({ hedgeData, selectedPair }) => {
  const label = PAIRS.find(p => p.pair === selectedPair)?.label || 'USD';
  if (!hedgeData) return null;
  const { netPosition, forwardFc, pcfcFc, importFc, totalHedged, unhedged, hedgePct } = hedgeData;

  const Bar = ({ fc, total, color, label: bLabel }) => {
    const pct = total > 0 ? Math.round(fc / total * 100) : 0;
    return (
      <div style={{ marginBottom: 6 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, marginBottom: 3 }}>
          <span style={{ color: C.muted }}>{bLabel}</span>
          <span style={{ fontWeight: 600, color }}>
            {fmtFC(fc)} &nbsp;
            <span style={{ color: C.muted, fontWeight: 400 }}>{pct}%</span>
          </span>
        </div>
        <div style={{ height: 8, background: C.surface, borderRadius: 4, overflow: 'hidden' }}>
          <div style={{ width: pct + '%', height: '100%', background: color, borderRadius: 4, transition: 'width 0.4s ease' }} />
        </div>
      </div>
    );
  };

  return (
    <div style={{ background: C.white, border: `0.5px solid ${C.border}`, borderRadius: 10, padding: 14, marginBottom: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: C.blue }}>Hedge Ratio — {label}</span>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 22, fontWeight: 800, color: hedgePct >= 70 ? C.green : hedgePct >= 40 ? C.amber : C.red }}>
            {hedgePct || 0}%
          </div>
          <div style={{ fontSize: 10, color: C.muted }}>hedged</div>
        </div>
      </div>
      <div style={{ fontSize: 11, color: C.muted, marginBottom: 10, paddingBottom: 8, borderBottom: `1px solid ${C.border}` }}>
        Net position (Export − Import): <strong style={{ color: C.navy }}>{fmtFC(netPosition)} FC</strong>
      </div>
      <Bar fc={forwardFc} total={netPosition} color="#1a5fa8" label="Forward cover" />
      <Bar fc={pcfcFc}    total={netPosition} color={C.purple} label="PCFC" />
      <Bar fc={importFc}  total={netPosition} color="#16A34A"  label="Import netting" />
      <div style={{ marginTop: 10, paddingTop: 8, borderTop: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between' }}>
        <div>
          <div style={{ fontSize: 10, color: C.muted }}>Total hedged</div>
          <div style={{ fontSize: 14, fontWeight: 700, color: C.green }}>{fmtFC(totalHedged)} FC</div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 10, color: C.muted }}>Unhedged</div>
          <div style={{ fontSize: 14, fontWeight: 700, color: unhedged > 0 ? C.red : C.green }}>{fmtFC(unhedged)} FC</div>
        </div>
      </div>
    </div>
  );
};

// ── OVERDUE BOX ──────────────────────────────────────────────────────────
const OverdueBox = ({ overdueBucket, spotRate }) => {
  if (!overdueBucket || overdueBucket.exportTotal === 0) return null;
  return (
    <div style={{ background: C.white, border: `1.5px solid ${C.red}`, borderRadius: 10, padding: 14, marginBottom: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: C.red }}>⚠ Overdue Bills</span>
          <Pill label={`${overdueBucket.billCount || 0} bills · ${fmtFC(overdueBucket.exportTotal)} FC`} bg="#fee2e2" color={C.red} />
        </div>
        <span style={{ fontSize: 12, fontWeight: 700, color: C.red }}>
          {spotRate > 0 ? fmtINR(overdueBucket.totalMtmInr) : '—'}
        </span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10 }}>
        <div style={{ background: '#fff7ed', borderRadius: 8, padding: '8px 12px', border: `0.5px solid #fed7aa` }}>
          <div style={{ fontSize: 10, color: C.muted, marginBottom: 2 }}>Eligible for discounting</div>
          <div style={{ fontSize: 14, fontWeight: 700, color: C.amber }}>{overdueBucket.eligibleDiscountCount || 0} bills</div>
          <div style={{ fontSize: 11, color: C.amber }}>{fmtFC(overdueBucket.eligibleDiscountFcAmt)} FC</div>
        </div>
        <div style={{ background: C.surface, borderRadius: 8, padding: '8px 12px', border: `0.5px solid ${C.border}` }}>
          <div style={{ fontSize: 10, color: C.muted, marginBottom: 2 }}>Free INR receivable</div>
          <div style={{ fontSize: 14, fontWeight: 700, color: C.amber }}>
            {spotRate > 0 ? fmtINR(overdueBucket.freeInrReceivable).replace('+', '') : '—'}
          </div>
          <div style={{ fontSize: 11, color: C.muted }}>at spot {fmtRate(spotRate)}</div>
        </div>
      </div>
    </div>
  );
};

// ── BUCKET CARD ──────────────────────────────────────────────────────────
const BucketCard = ({ bkt, spotRate }) => {
  const isCurrent   = bkt.bucket === 'Current';
  const totalExport = (bkt.exportShipped || 0) + (bkt.exportUnshipped || 0);
  const shipPct     = totalExport > 0 ? (bkt.exportShipped / totalExport) * 100 : 0;
  const unshipPct   = 100 - shipPct;

  return (
    <div style={{ background: C.white, border: `0.5px solid ${C.border}`, borderRadius: 10, overflow: 'hidden' }}>
      {/* Header */}
      <div style={{ background: isCurrent ? C.navy : '#1a2f47', padding: '8px 12px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: C.white }}>
            {bkt.dateFrom} – {bkt.dateTo}
          </span>
          <Pill
            label={bkt.bucket}
            bg={isCurrent ? 'rgba(201,168,76,0.25)' : 'rgba(255,255,255,0.1)'}
            color={isCurrent ? C.gold : 'rgba(255,255,255,0.6)'}
          />
        </div>
        {/* 3 key stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 4, marginTop: 4 }}>
          {[
            { label: 'Total FC',  value: fmtFC(totalExport),                        color: C.white   },
            { label: 'Avg Cost',  value: fmtRate(bkt.avgCostRate),                  color: '#93c5fd' },
            { label: 'Fwd Rate',  value: bkt.fwdRate ? fmtRate(bkt.fwdRate) : '—', color: C.gold    },
          ].map(item => (
            <div key={item.label}>
              <div style={{ fontSize: 8, color: 'rgba(255,255,255,0.5)', letterSpacing: 0.5 }}>{item.label}</div>
              <div style={{ fontSize: 11, fontWeight: 700, color: item.color }}>{item.value}</div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ padding: 10 }}>
        {/* PCFC Alert */}
        {bkt.pcfcAlert && (
          <div style={{
            marginBottom: 8, padding: '4px 8px', borderRadius: 6,
            background: bkt.pcfcAlert.alertLevel === 'URGENT' ? '#fee2e2' : '#ede9fe',
            border: `0.5px solid ${bkt.pcfcAlert.alertLevel === 'URGENT' ? C.red : '#7c3aed'}`,
            display: 'flex', justifyContent: 'space-between', alignItems: 'center'
          }}>
            <span style={{ fontSize: 10, fontWeight: 500, color: bkt.pcfcAlert.alertLevel === 'URGENT' ? C.red : C.purple }}>
              PCFC: {bkt.pcfcAlert.trancheId}
            </span>
            <span style={{ fontSize: 11, fontWeight: 700, color: bkt.pcfcAlert.alertLevel === 'URGENT' ? C.red : C.purple }}>
              {bkt.pcfcAlert.daysLeft}d left
            </span>
          </div>
        )}

        {/* Export bar */}
        <div style={{ marginBottom: 8 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: C.muted, marginBottom: 3 }}>
            <span>Export</span>
            <span>{fmtFC(totalExport)} FC</span>
          </div>
          <div style={{ height: 18, borderRadius: 4, background: C.surface, overflow: 'hidden', display: 'flex' }}>
            {shipPct > 0 && (
              <div style={{ width: shipPct + '%', background: '#1a5fa8', display: 'flex', alignItems: 'center', padding: '0 4px' }}>
                {shipPct > 30 && <span style={{ fontSize: 9, color: C.white, whiteSpace: 'nowrap' }}>{fmtFC(bkt.exportShipped)}</span>}
              </div>
            )}
            {unshipPct > 0 && bkt.exportUnshipped > 0 && (
              <div style={{ width: unshipPct + '%', background: '#d0e8f8', display: 'flex', alignItems: 'center', padding: '0 4px' }}>
                {unshipPct > 30 && <span style={{ fontSize: 9, color: '#1a5fa8', whiteSpace: 'nowrap' }}>{fmtFC(bkt.exportUnshipped)}</span>}
              </div>
            )}
          </div>
        </div>

        {/* Import / Fwd / PCFC pills */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 8 }}>
          {[
            { label: 'Import',    val: bkt.imports,      bg: '#fdedc0', color: C.amber  },
            { label: 'Fwd cover', val: bkt.forwardCover, bg: '#c8f0d8', color: C.green  },
            { label: 'PCFC due',  val: bkt.pcfcDue,      bg: '#ede9fe', color: C.purple },
          ].map((pill, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 10, color: C.muted, width: 64, flexShrink: 0 }}>{pill.label}</span>
              {pill.val > 0 ? (
                <span style={{ fontSize: 11, fontWeight: 500, padding: '1px 8px', borderRadius: 20, background: pill.bg, color: pill.color }}>
                  {fmtFC(pill.val)}
                </span>
              ) : (
                <span style={{ fontSize: 11, color: '#cbd5e1' }}>—</span>
              )}
            </div>
          ))}
        </div>

        {/* Open position */}
        <div style={{ background: C.surface, borderRadius: 6, padding: '4px 8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
          <span style={{ fontSize: 11, color: C.muted }}>Open position</span>
          <span style={{ fontSize: 12, fontWeight: 700, color: C.navy }}>{fmtFC(bkt.openPosition)} FC</span>
        </div>

        {/* MTM */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <span style={{ fontSize: 11, color: C.muted }}>Total MTM</span>
          <span style={{
            fontSize: 12, fontWeight: 700, padding: '2px 8px', borderRadius: 20,
            background: spotRate > 0 ? (bkt.totalMtmInr >= 0 ? '#c8f0d8' : '#fee2e2') : C.surface,
            color: spotRate > 0 ? (bkt.totalMtmInr >= 0 ? C.green : C.red) : C.muted
          }}>
            {spotRate > 0 ? fmtINR(bkt.totalMtmInr) : '—'}
          </span>
        </div>

        {/* Cashflow */}
        <div style={{ borderTop: `0.5px solid ${C.surface}`, paddingTop: 8 }}>
          {bkt.eligibleDiscountCount > 0 && (
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              background: '#eff6ff', border: `0.5px solid #93c5fd`, borderRadius: 6,
              padding: '3px 8px', marginBottom: 6
            }}>
              <span style={{ fontSize: 10, color: '#1e40af', fontWeight: 500 }}>Eligible for discounting</span>
              <span style={{ fontSize: 11, fontWeight: 600, color: '#1e40af' }}>
                {bkt.eligibleDiscountCount} · {fmtFC(bkt.eligibleDiscountFcAmt)}
              </span>
            </div>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ fontSize: 10, color: C.muted }}>Free INR receivable</span>
              <span style={{ fontSize: 11, fontWeight: 500, color: C.amber }}>
                {spotRate > 0 ? fmtINR(bkt.freeInrReceivable).replace('+', '') : '—'}
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ fontSize: 10, color: C.muted }}>INR outflow</span>
              <span style={{ fontSize: 11, fontWeight: 500, color: C.red }}>
                {spotRate > 0 && bkt.inrOutflow > 0 ? '−' + fmtINR(bkt.inrOutflow).replace(/[+\u2212]/g, '') : '—'}
              </span>
            </div>
            <div style={{ background: C.surface, borderRadius: 6, padding: '3px 8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 2 }}>
              <span style={{ fontSize: 11, fontWeight: 600, color: '#374151' }}>Net cashflow</span>
              <span style={{ fontSize: 12, fontWeight: 700, color: spotRate > 0 ? (bkt.netCashflowInr >= 0 ? C.green : C.red) : C.muted }}>
                {spotRate > 0 ? fmtINR(bkt.netCashflowInr) : '—'}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── BILLS TABLE ──────────────────────────────────────────────────────────
const BillsTable = ({ bills, spotRate, selectedPair }) => {
  const label = PAIRS.find(p => p.pair === selectedPair)?.label || '';
  const rowStyle = (b) => {
    if (b.isOverdue) return { background: '#fff1f1', borderLeft: `3px solid ${C.red}`   };
    if (b.isShipped) return { background: '#f0fdf4', borderLeft: `3px solid ${C.green}` };
    return                  { background: '#fffbeb', borderLeft: `3px solid #F59E0B`    };
  };
  const statusDot = (b) => {
    if (b.isOverdue) return { color: C.red,    label: '⚠ Overdue' };
    if (b.isShipped) return { color: C.green,  label: '● Shipped'  };
    return                  { color: '#F59E0B', label: '● Pending'  };
  };
  return (
    <div style={{ marginBottom: 20 }}>
      <SectionTitle text={`Bills — orders and invoices (${label})`} />
      <div style={{ background: C.white, border: `0.5px solid ${C.border}`, borderRadius: 10, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto', maxHeight: 300, overflowY: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ background: C.navy, position: 'sticky', top: 0 }}>
                {['Ref No', 'Customer', 'FC Amt', 'Cost Rate', 'Fwd Rate', 'Due Date', 'MTM'].map(h => (
                  <th key={h} style={{
                    padding: '7px 10px',
                    textAlign: h === 'Ref No' || h === 'Customer' ? 'left' : 'right',
                    fontSize: 10, fontWeight: 600, color: 'rgba(255,255,255,0.7)',
                    whiteSpace: 'nowrap', textTransform: 'uppercase', letterSpacing: 0.4
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {bills.length === 0 ? (
                <tr><td colSpan={7} style={{ padding: 14, color: C.muted, fontSize: 12 }}>No open bills for {label}</td></tr>
              ) : bills.map((b, i) => {
                const st = statusDot(b);
                return (
                  <tr key={i} style={{ ...rowStyle(b), borderBottom: `0.5px solid ${C.border}` }}>
                    <td style={{ padding: '7px 10px', fontWeight: 600 }}>
                      <span style={{ color: st.color, fontSize: 10, marginRight: 4 }}>{st.label}</span>
                      <br />
                      <span style={{ color: C.navy, fontSize: 12 }}>{b.refNo}</span>
                    </td>
                    <td style={{ padding: '7px 10px', color: C.navy }}>{b.customer}</td>
                    <td style={{ padding: '7px 10px', textAlign: 'right', fontWeight: 600 }}>{fmtFC(b.fcAmount)}</td>
                    <td style={{ padding: '7px 10px', textAlign: 'right', color: C.muted }}>{fmtRate(b.costRate)}</td>
                    <td style={{ padding: '7px 10px', textAlign: 'right', color: C.amber }}>{b.fwdRateUsed ? fmtRate(b.fwdRateUsed) : '—'}</td>
                    <td style={{ padding: '7px 10px', color: b.isOverdue ? C.red : C.navy, whiteSpace: 'nowrap' }}>
                      {b.dueDate}{b.isEstimated ? ' (est)' : ''}
                    </td>
                    <td style={{ padding: '7px 10px', textAlign: 'right', fontWeight: 600, color: spotRate > 0 ? (b.mtmInr >= 0 ? C.green : C.red) : C.muted }}>
                      {spotRate > 0 ? fmtINR(b.mtmInr) : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ══════════════════════════════════════════════════════════════════════════
export default function DashboardPage({ token }) {
  const [selectedPair, setSelectedPair] = useState('USDINR');
  const [kpis2,        setKpis2]        = useState(null);
  const [buckets,      setBuckets]      = useState([]);
  const [bills,        setBills]        = useState([]);
  const [forwards,     setForwards]     = useState([]);
  const [pcfc,         setPcfc]         = useState([]);
  const [liveRates,    setLiveRates]    = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [refreshing,   setRefreshing]   = useState(false);
  const [lastRefresh,  setLastRefresh]  = useState(null);
  const [error,        setError]        = useState('');

  const liveRef   = useRef(null);
  const staticRef = useRef(null);
  const hdr = { headers: { authorization: 'Bearer ' + token } };

  // ── Spot rate from live rates ────────────────────────────────────────
  const spotRate = liveRates?.[selectedPair]?.ltp || 0;

  // ── Fetch live rates from fx.fxcapitalindia.in — every 10 seconds ───
  const fetchLiveRates = useCallback(async () => {
    try {
      const res = await axios.get(`${FX_API}/rates/live`);
      if (!res.data || !Array.isArray(res.data)) return;
      const mapped = {};
      res.data.forEach(r => {
        if (!r.currencyPair) return;
        if (!['USDINR','EURINR','GBPINR','AEDINR'].includes(r.currencyPair)) return;
        mapped[r.currencyPair] = {
          pair:   r.currencyPair,
          bid:    r.bid,
          ask:    r.ask,
          ltp:    r.ltp,
          prev:   r.close,
          change: r.netChange,
        };
      });
      if (Object.keys(mapped).length > 0) setLiveRates(mapped);
    } catch {}
  }, []);

  // ── Fetch static data — buckets, bills, forwards, pcfc, kpis2 ───────
  // Each call has its own .catch() so one failure never blocks the others
  const fetchStatic = useCallback(async (pair) => {
    try {
      setError('');
      const [bktRes, billRes, fwdRes, pcfcRes, kpi2Res] = await Promise.all([
        axios.get(`${BILL}/dashboard/buckets?pair=${pair}`,  hdr).catch(() => ({ data: { buckets:  [] } })),
        axios.get(`${BILL}/dashboard/bills?pair=${pair}`,    hdr).catch(() => ({ data: { bills:    [] } })),
        axios.get(`${BILL}/dashboard/forwards?pair=${pair}`, hdr).catch(() => ({ data: { forwards: [] } })),
        axios.get(`${BILL}/dashboard/pcfc?pair=${pair}`,     hdr).catch(() => ({ data: { pcfc:     [] } })),
        // kpis2 — optional — fails gracefully, does not block other data
        axios.get(`${BILL}/dashboard/kpis2`, hdr).catch(() => ({ data: null })),
      ]);
      setBuckets(bktRes.data?.buckets   || []);
      setBills(billRes.data?.bills       || []);
      setForwards(fwdRes.data?.forwards  || []);
      setPcfc(pcfcRes.data?.pcfc         || []);
      if (kpi2Res.data) setKpis2(kpi2Res.data);
      setLastRefresh(new Date());
    } catch (e) {
      setError('Failed to load data. Check connection.');
    }
  }, [token]);

  // ── On mount ────────────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      setLoading(true);
      await Promise.all([fetchStatic(selectedPair), fetchLiveRates()]);
      setLoading(false);
    })();
    liveRef.current   = setInterval(fetchLiveRates, 10000);
    staticRef.current = setInterval(() => fetchStatic(selectedPair), 60000);
    return () => { clearInterval(liveRef.current); clearInterval(staticRef.current); };
  }, []);

  // ── Currency change ──────────────────────────────────────────────────
  useEffect(() => {
    if (!loading) {
      setRefreshing(true);
      fetchStatic(selectedPair).finally(() => setRefreshing(false));
    }
  }, [selectedPair]);

  // ── Manual refresh ───────────────────────────────────────────────────
  const handleRefresh = async () => {
    setRefreshing(true);
    setError('');
    await Promise.all([fetchStatic(selectedPair), fetchLiveRates()]);
    setRefreshing(false);
  };

  const overdueBucket = buckets.find(b => b.bucket === 'Overdue');
  const mainBuckets   = buckets.filter(b => b.bucket !== 'Overdue');
  const hedgeData     = kpis2?.hedgeRatio?.[selectedPair] || null;

  if (loading) return (
    <div className="content">
      <p style={{ padding: 20, color: C.muted }}>Loading dashboard…</p>
    </div>
  );

  return (
    <div style={{ background: '#f1f5f9', minHeight: '100vh' }}>

      {/* LIVE RATE STRIP — from fx.fxcapitalindia.in */}
      <RateStrip rates={liveRates} />

      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '12px 12px 32px' }}>

        {error && (
          <div style={{ background: '#fee2e2', color: C.red, padding: '8px 14px', borderRadius: 6, marginBottom: 12, fontSize: 13 }}>
            {error}
          </div>
        )}

        {/* ALL CURRENCY EXPOSURE — kpis2 optional */}
        <ExposureTable kpis2={kpis2} rates={liveRates} />

        {/* CURRENCY TABS + REFRESH */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: C.blue }}>Exposure ladder</span>
          <div style={{ display: 'flex', gap: 6, marginLeft: 8 }}>
            {PAIRS.map(p => (
              <button key={p.pair} onClick={() => setSelectedPair(p.pair)} style={{
                padding: '4px 14px', border: `1px solid ${C.border}`,
                borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                background: selectedPair === p.pair ? C.blue : C.white,
                color:      selectedPair === p.pair ? C.white : C.muted
              }}>{p.label}</button>
            ))}
          </div>
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10 }}>
            {lastRefresh && (
              <span style={{ fontSize: 11, color: C.muted }}>
                Updated {lastRefresh.toLocaleTimeString('en-IN')}
              </span>
            )}
            <button onClick={handleRefresh} disabled={refreshing} style={{
              fontSize: 12, padding: '5px 14px', borderRadius: 6,
              border: `1px solid ${C.border}`,
              background: refreshing ? C.surface : C.blue,
              color:      refreshing ? C.muted   : C.white,
              cursor: refreshing ? 'not-allowed' : 'pointer', fontWeight: 600
            }}>
              {refreshing ? 'Refreshing…' : 'Refresh'}
            </button>
          </div>
        </div>

        {/* HEDGE RATIO — only if kpis2 loaded */}
        <HedgeRatio hedgeData={hedgeData} selectedPair={selectedPair} />

        {/* LEGEND */}
        <div style={{ display: 'flex', gap: 14, marginBottom: 12, flexWrap: 'wrap' }}>
          {[
            { color: '#1a5fa8',              label: 'Shipped'       },
            { color: '#d0e8f8', border: '#7fb3e8', label: 'Not shipped' },
            { color: '#fdedc0', border: '#c47a00', label: 'Import'       },
            { color: '#c8f0d8', border: '#2e9e57', label: 'Forward cover' },
            { color: '#ede9fe', border: '#7c3aed', label: 'PCFC due'     },
          ].map((l, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: C.muted }}>
              <div style={{ width: 10, height: 10, borderRadius: 2, flexShrink: 0, background: l.color, border: l.border ? `1px solid ${l.border}` : 'none' }} />
              {l.label}
            </div>
          ))}
        </div>

        {/* OVERDUE */}
        <OverdueBox overdueBucket={overdueBucket} spotRate={spotRate} />

        {/* BUCKET GRID — 6 two-week buckets */}
        {mainBuckets.length === 0 ? (
          <div style={{ background: C.white, border: `0.5px solid ${C.border}`, borderRadius: 10, padding: 20, marginBottom: 24, color: C.muted, fontSize: 13 }}>
            No exposure data for {PAIRS.find(p => p.pair === selectedPair)?.label}. Click Refresh to reload.
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 24 }}>
            {mainBuckets.map((bkt, i) => (
              <BucketCard key={i} bkt={bkt} spotRate={spotRate} />
            ))}
          </div>
        )}

        {/* BILLS TABLE */}
        <BillsTable bills={bills} spotRate={spotRate} selectedPair={selectedPair} />

        {/* FORWARDS + PCFC */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>

          <div>
            <SectionTitle text="Forward contracts" />
            <div style={{ background: C.white, border: `0.5px solid ${C.border}`, borderRadius: 10, overflow: 'hidden' }}>
              <div style={{ overflowX: 'auto', maxHeight: 260, overflowY: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: C.navy }}>
                      {['Bank', 'FC Avail', 'Window End', 'Bucket', 'Booked Rate', 'MTM'].map(h => (
                        <th key={h} style={{ padding: '7px 10px', textAlign: 'left', fontSize: 10, fontWeight: 600, color: 'rgba(255,255,255,0.7)', whiteSpace: 'nowrap', textTransform: 'uppercase', letterSpacing: 0.4 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {forwards.length === 0 ? (
                      <tr><td colSpan={6} style={{ padding: 14, color: C.muted, fontSize: 12 }}>No open forwards for {PAIRS.find(p => p.pair === selectedPair)?.label}</td></tr>
                    ) : forwards.map((f, i) => (
                      <tr key={i} style={{ borderBottom: `0.5px solid ${C.surface}`, background: i % 2 === 0 ? C.white : C.surface }}>
                        <td style={{ padding: '7px 10px' }}>{f.bank}</td>
                        <td style={{ padding: '7px 10px', textAlign: 'right' }}>{fmtFC(f.fcAvailable)}</td>
                        <td style={{ padding: '7px 10px', whiteSpace: 'nowrap' }}>{f.windowEnd}</td>
                        <td style={{ padding: '7px 10px' }}><Pill label={f.bucket} bg="#dbeafe" color="#1e40af" /></td>
                        <td style={{ padding: '7px 10px', textAlign: 'right' }}>{fmtRate(f.bookedRate)}</td>
                        <td style={{ padding: '7px 10px', textAlign: 'right', fontWeight: 600, color: spotRate > 0 ? (f.mtmInr >= 0 ? C.green : C.red) : C.muted }}>
                          {spotRate > 0 ? fmtINR(f.mtmInr) : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div>
            <SectionTitle text="PCFC obligations" />
            <div style={{ background: C.white, border: `0.5px solid ${C.border}`, borderRadius: 10, overflow: 'hidden' }}>
              <div style={{ overflowX: 'auto', maxHeight: 260, overflowY: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: C.navy }}>
                      {['Tranche', 'Bank', 'FC Amt', 'Draw Rate', 'Due Date', 'Days', 'MTM'].map(h => (
                        <th key={h} style={{ padding: '7px 10px', textAlign: 'left', fontSize: 10, fontWeight: 600, color: 'rgba(255,255,255,0.7)', whiteSpace: 'nowrap', textTransform: 'uppercase', letterSpacing: 0.4 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {pcfc.length === 0 ? (
                      <tr><td colSpan={7} style={{ padding: 14, color: C.muted, fontSize: 12 }}>No open PCFC for {PAIRS.find(p => p.pair === selectedPair)?.label}</td></tr>
                    ) : pcfc.map((p, i) => (
                      <tr key={i} style={{ borderBottom: `0.5px solid ${C.surface}`, background: i % 2 === 0 ? C.white : C.surface }}>
                        <td style={{ padding: '7px 10px', fontWeight: 600, color: p.alertLevel === 'URGENT' ? C.red : p.alertLevel === 'WARNING' ? C.amber : C.navy }}>
                          {p.trancheId}
                        </td>
                        <td style={{ padding: '7px 10px' }}>{p.bank}</td>
                        <td style={{ padding: '7px 10px', textAlign: 'right' }}>{fmtFC(p.fcAmount)}</td>
                        <td style={{ padding: '7px 10px', textAlign: 'right' }}>{fmtRate(p.drawdownRate)}</td>
                        <td style={{ padding: '7px 10px', whiteSpace: 'nowrap' }}>{p.dueDate}</td>
                        <td style={{ padding: '7px 10px', textAlign: 'right' }}>
                          <span style={{ fontSize: 11, fontWeight: 700, color: p.alertLevel === 'URGENT' ? C.red : p.alertLevel === 'WARNING' ? C.amber : C.green }}>
                            {p.daysRemaining}d{p.alertLevel === 'URGENT' ? ' !' : ''}
                          </span>
                        </td>
                        <td style={{ padding: '7px 10px', textAlign: 'right', fontWeight: 600, color: spotRate > 0 ? (p.mtmInr >= 0 ? C.green : C.red) : C.muted }}>
                          {spotRate > 0 ? fmtINR(p.mtmInr) : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}